import axios from 'axios'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { OrganizationIntegrationStatus } from 'src/enums'
import Trns from 'src/localization/Trns'
import { BankAccountListResponse } from 'src/models/bankAccount.models'
import { OrganizationIntegrationListResponse } from 'src/models/organizationIntegration.models'
import { TaxRateListResponse } from 'src/models/taxRate.models'
import { UserListResponse } from 'src/models/user.models'
import { closeModal } from 'src/navigation/Lnk'
import { orgIntegrationsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import { createSetIsManageOrgIntegrationPopupOpen } from 'src/redux/actions/app.actions'
import { getOrgIntegrations } from 'src/redux/actions/data/orgIntegrations.actions'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const MANAGE_ORG_INTEGRATION_GET_STATUS_INIT =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_STATUS_INIT'
export const MANAGE_ORG_INTEGRATION_GET_STATUS_SUCCESS =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_STATUS_SUCCESS'
export const MANAGE_ORG_INTEGRATION_GET_STATUS_FAILURE =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_STATUS_FAILURE'

export type ManageOrgIntegrationGetStatusSuccessPayload = {
  status: OrganizationIntegrationStatus
}

export type ManageOrgIntegrationGetStatusFailurePayload =
  BackendErrorPayload<string>

export interface ManageOrgIntegrationGetStatusInitAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_STATUS_INIT
}

export interface ManageOrgIntegrationGetStatusSuccessAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_STATUS_SUCCESS
  payload: ManageOrgIntegrationGetStatusSuccessPayload
}

export interface ManageOrgIntegrationGetStatusFailureAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_STATUS_FAILURE
  payload: ManageOrgIntegrationGetStatusFailurePayload
}

export type ManageOrgIntegrationGetStatusActions =
  | ManageOrgIntegrationGetStatusInitAction
  | ManageOrgIntegrationGetStatusSuccessAction
  | ManageOrgIntegrationGetStatusFailureAction

const manageOrgIntegrationGetStatusInitAction =
  (): ManageOrgIntegrationGetStatusInitAction => ({
    type: MANAGE_ORG_INTEGRATION_GET_STATUS_INIT,
  })

const manageOrgIntegrationGetStatusSuccessAction = (
  payload: ManageOrgIntegrationGetStatusSuccessPayload,
): ManageOrgIntegrationGetStatusSuccessAction => ({
  type: MANAGE_ORG_INTEGRATION_GET_STATUS_SUCCESS,
  payload,
})

const manageOrgIntegrationGetStatusFailureAction = (
  payload: ManageOrgIntegrationGetStatusFailurePayload,
): ManageOrgIntegrationGetStatusFailureAction => ({
  type: MANAGE_ORG_INTEGRATION_GET_STATUS_FAILURE,
  payload,
})

export const getIntegrationStatus = (
  orgId: string,
  connectionId: string,
  callback?: () => void,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  ManageOrgIntegrationGetStatusActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      ManageOrgIntegrationGetStatusActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(manageOrgIntegrationGetStatusInitAction())
    return axios
      .get<ManageOrgIntegrationGetStatusSuccessPayload>(
        `/organizations/${orgId}/connections/${connectionId}/status`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(manageOrgIntegrationGetStatusSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            manageOrgIntegrationGetStatusFailureAction(err.response.data),
          )
        },
      )
      .then(() => callback?.())
  }
}

export const MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_INIT =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_INIT'
export const MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_SUCCESS =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_SUCCESS'
export const MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_FAILURE =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_FAILURE'

export type ManageOrgIntegrationGetAccountsSuccessPayload = {
  bankAccounts: BankAccountListResponse[]
  taxRates?: Record<string, TaxRateListResponse>
}

export type ManageOrgIntegrationGetAccountsFailurePayload =
  BackendErrorPayload<string>

export interface ManageOrgIntegrationGetAccountsInitAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_INIT
}

export interface ManageOrgIntegrationGetAccountsSuccessAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_SUCCESS
  payload: ManageOrgIntegrationGetAccountsSuccessPayload
}

export interface ManageOrgIntegrationGetAccountsFailureAction {
  type: typeof MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_FAILURE
  payload: ManageOrgIntegrationGetAccountsFailurePayload
}

export type ManageOrgIntegrationGetAccountsActions =
  | ManageOrgIntegrationGetAccountsInitAction
  | ManageOrgIntegrationGetAccountsSuccessAction
  | ManageOrgIntegrationGetAccountsFailureAction

const manageOrgIntegrationGetAccountsInitAction =
  (): ManageOrgIntegrationGetAccountsInitAction => ({
    type: MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_INIT,
  })

const manageOrgIntegrationGetAccountsSuccessAction = (
  payload: ManageOrgIntegrationGetAccountsSuccessPayload,
): ManageOrgIntegrationGetAccountsSuccessAction => ({
  type: MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_SUCCESS,
  payload,
})

const manageOrgIntegrationGetAccountsFailureAction = (
  payload: ManageOrgIntegrationGetAccountsFailurePayload,
): ManageOrgIntegrationGetAccountsFailureAction => ({
  type: MANAGE_ORG_INTEGRATION_GET_ACCOUNTS_FAILURE,
  payload,
})

export const startIntegrationConfiguration = (
  orgId: string,
  connectionId: string,
  callback?: () => void,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  ManageOrgIntegrationGetAccountsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      ManageOrgIntegrationGetAccountsActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(manageOrgIntegrationGetAccountsInitAction())
    return axios
      .get<ManageOrgIntegrationGetAccountsSuccessPayload>(
        `/organizations/${orgId}/connections/${connectionId}/configure`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(manageOrgIntegrationGetAccountsSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            manageOrgIntegrationGetAccountsFailureAction(err.response.data),
          )
        },
      )
      .then(() => callback?.())
  }
}

export const MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_INIT =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_INIT'
export const MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_SUCCESS =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_SUCCESS'
export const MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_FAILURE =
  'app/ManageOrgIntegration/MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_FAILURE'

export type ManageOrgIntegrationAddAccountSuccessPayload = {
  organizationConnection: OrganizationIntegrationListResponse
}

export type ManageOrgIntegrationAddAccountFailurePayload =
  BackendErrorPayload<string>

export interface ManageOrgIntegrationAddAccountInitAction {
  type: typeof MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_INIT
}
export interface ManageOrgIntegrationAddAccountSuccessAction {
  type: typeof MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_SUCCESS
  payload: ManageOrgIntegrationAddAccountSuccessPayload
}
export interface ManageOrgIntegrationAddAccountFailureAction {
  type: typeof MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_FAILURE
  payload: ManageOrgIntegrationAddAccountFailurePayload
}

export type ManageOrgIntegrationAddAccountActions =
  | ManageOrgIntegrationAddAccountInitAction
  | ManageOrgIntegrationAddAccountSuccessAction
  | ManageOrgIntegrationAddAccountFailureAction

const manageOrgIntegrationAddAccountInitAction =
  (): ManageOrgIntegrationAddAccountInitAction => ({
    type: MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_INIT,
  })

const manageOrgIntegrationAddAccountSuccessAction = (
  payload: ManageOrgIntegrationAddAccountSuccessPayload,
): ManageOrgIntegrationAddAccountSuccessAction => ({
  type: MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_SUCCESS,
  payload,
})

const manageOrgIntegrationAddAccountFailureAction = (
  payload: ManageOrgIntegrationAddAccountFailurePayload,
): ManageOrgIntegrationAddAccountFailureAction => ({
  type: MANAGE_ORG_INTEGRATION_ADD_ACCOUNT_FAILURE,
  payload,
})

export const manageOrganizationAddAccountIntegration = (
  orgId: string,
  connectionId: string,
  bankAccountId: string,
  taxRateId: string | undefined,
  connectedPlatformName: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  ManageOrgIntegrationAddAccountActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      ManageOrgIntegrationAddAccountActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(manageOrgIntegrationAddAccountInitAction())
    return axios
      .post<ManageOrgIntegrationAddAccountSuccessPayload>(
        `/organizations/${orgId}/connections/${connectionId}/configure`,
        { bankAccountToken: bankAccountId, taxRateId: taxRateId },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(manageOrgIntegrationAddAccountSuccessAction(response.data))
          dispatch(createSetIsManageOrgIntegrationPopupOpen(false))
          dispatch(getOrgIntegrations(orgId))
          queryClient.invalidateQueries({
            queryKey: orgIntegrationsQueryKeys.all,
          })
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <Trns
                  source={
                    'Success! You have integrated your organization with ${connectedPlatform}.'
                  }
                  expressions={{
                    connectedPlatform: connectedPlatformName,
                  }}
                />
              ),
            }),
          )
        },
        (err) => {
          dispatch(
            manageOrgIntegrationAddAccountFailureAction(err.response.data),
          )
        },
      )
  }
}

export const DELETE_USERS_ORG_INTEGRATION_LOGIN_INIT =
  'data/ManageOrgIntegration/DELETE_USERS_ORG_INTEGRATION_LOGIN_INIT'
export const DELETE_USERS_ORG_INTEGRATION_LOGIN_SUCCESS =
  'data/ManageOrgIntegration/DELETE_USERS_ORG_INTEGRATION_LOGIN_SUCCESS'
export const DELETE_USERS_ORG_INTEGRATION_LOGIN_FAILURE =
  'data/ManageOrgIntegration/DELETE_USERS_ORG_INTEGRATION_LOGIN_FAILURE'

type DeleteUsersOrgIntegrationLoginSuccessPayload = UserListResponse

type DeleteUsersOrgIntegrationLoginFailurePayload = BackendErrorPayload<string>

interface DeleteUsersOrgIntegrationLoginInitAction {
  type: typeof DELETE_USERS_ORG_INTEGRATION_LOGIN_INIT
}
interface DeleteUsersOrgIntegrationLoginSuccessAction {
  type: typeof DELETE_USERS_ORG_INTEGRATION_LOGIN_SUCCESS
  payload: DeleteUsersOrgIntegrationLoginSuccessPayload
}
interface DeleteUsersOrgIntegrationLoginFailureAction {
  type: typeof DELETE_USERS_ORG_INTEGRATION_LOGIN_FAILURE
  payload: DeleteUsersOrgIntegrationLoginFailurePayload
}

export type DeleteUsersOrgIntegrationLoginActions =
  | DeleteUsersOrgIntegrationLoginInitAction
  | DeleteUsersOrgIntegrationLoginSuccessAction
  | DeleteUsersOrgIntegrationLoginFailureAction

const createDeleteUsersOrgIntegrationLoginInitAction =
  (): DeleteUsersOrgIntegrationLoginInitAction => ({
    type: DELETE_USERS_ORG_INTEGRATION_LOGIN_INIT,
  })

const createDeleteUsersOrgIntegrationLoginSuccessAction = (
  payload: DeleteUsersOrgIntegrationLoginSuccessPayload,
): DeleteUsersOrgIntegrationLoginSuccessAction => ({
  type: DELETE_USERS_ORG_INTEGRATION_LOGIN_SUCCESS,
  payload,
})

const createDeleteUsersOrgIntegrationLoginFailureAction = (
  payload: DeleteUsersOrgIntegrationLoginFailurePayload,
): DeleteUsersOrgIntegrationLoginFailureAction => ({
  type: DELETE_USERS_ORG_INTEGRATION_LOGIN_FAILURE,
  payload,
})

export const deleteOrgIntegrationLogin = (
  orgId: string,
  connectionId: string,
  connectedPlatformName: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  DeleteUsersOrgIntegrationLoginActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      DeleteUsersOrgIntegrationLoginActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteUsersOrgIntegrationLoginInitAction())
    return axios
      .delete<DeleteUsersOrgIntegrationLoginSuccessPayload>(
        `/organizations/${orgId}/connections/${connectionId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createDeleteUsersOrgIntegrationLoginSuccessAction(response.data),
          )
          queryClient.invalidateQueries({
            queryKey: orgIntegrationsQueryKeys.all,
          })
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <Trns
                  source={
                    'Success! You have disconnected your organization from ${connectedPlatform}.'
                  }
                  expressions={{
                    connectedPlatform: connectedPlatformName,
                  }}
                />
              ),
            }),
          )
          dispatch(createSetIsManageOrgIntegrationPopupOpen(false))
        },
        (err) => {
          dispatch(
            createDeleteUsersOrgIntegrationLoginFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}
